import React from 'react';
import { Link } from 'react-router-dom';
import { useSettings } from '../context/SettingsContext';
import { Facebook, Twitter, Instagram, Youtube, Linkedin, MapPin, Music2 } from 'lucide-react';

const SOCIAL_LINKS = [
  { key: 'social_facebook', icon: Facebook, label: 'Facebook' },
  { key: 'social_instagram', icon: Instagram, label: 'Instagram' },
  { key: 'social_twitter', icon: Twitter, label: 'X / Twitter' },
  { key: 'social_linkedin', icon: Linkedin, label: 'LinkedIn' },
  { key: 'social_youtube', icon: Youtube, label: 'YouTube' },
  { key: 'social_tiktok', icon: Music2, label: 'TikTok' },
];

const slugify = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const routeFor = (entry) => {
  // entry may be a plain string (legacy) or { label, slug, href }
  if (!entry) return { label: '', href: '#' };
  if (typeof entry === 'string') return { label: entry, href: `/pages/${slugify(entry)}` };
  if (entry.href) return { label: entry.label || entry.slug, href: entry.href };
  if (entry.slug) {
    // Special-case a couple of slugs to existing app routes.
    if (entry.slug === 'track-order') return { label: entry.label, href: '/orders' };
    if (entry.slug === 'trade-point') return { label: entry.label, href: '/login?role=retailer' };
    return { label: entry.label, href: `/pages/${entry.slug}` };
  }
  return { label: entry.label || 'Link', href: '#' };
};

const Footer = () => {
  const { settings } = useSettings();
  const footerLinks = settings.footer_links || {};
  const brandName = settings.brand_name || 'Dr Tools';
  const socials = SOCIAL_LINKS.filter(s => (settings[s.key] || '').trim());
  return (
    <footer className="bg-[#12263A] text-white mt-16">
      <div className="max-w-[1400px] mx-auto px-4 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {Object.entries(footerLinks).map(([title, links]) => (
            <div key={title}>
              <h4 className="font-bold text-base mb-4">{title}</h4>
              <ul className="space-y-2">
                {links.map((entry, idx) => {
                  const { label, href } = routeFor(entry);
                  const isExternal = /^(mailto:|https?:|tel:)/.test(href);
                  return (
                    <li key={`${label}-${idx}`}>
                      {isExternal ? (
                        <a href={href} className="text-sm text-neutral-300 hover:text-[#F46A25]">{label}</a>
                      ) : (
                        <Link to={href} className="text-sm text-neutral-300 hover:text-[#F46A25]" data-testid={`footer-link-${slugify(label)}`}>{label}</Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-10 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            {settings.logo_url ? (
              <div className="bg-white h-12 flex items-center px-2 rounded-sm">
                <img src={settings.logo_url} alt={brandName} className="h-full w-auto object-contain max-w-[140px]" />
              </div>
            ) : (
              <div className="bg-[#F46A25] text-white font-black text-lg h-12 px-3 flex items-center justify-center rounded-sm">{brandName}</div>
            )}
            <div className="text-xs text-neutral-400" data-testid="footer-company-block">
              {settings.company_address ? (
                <div className="flex items-start gap-1 max-w-[280px] whitespace-pre-line leading-tight">
                  <MapPin className="w-3 h-3 mt-0.5 shrink-0" /> {settings.company_address}
                </div>
              ) : (
                <div className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {brandName} UK, Chandlers Ford</div>
              )}
              <div className="mt-1">
                {settings.company_phone && <span className="mr-2">☎ {settings.company_phone}</span>}
                {settings.company_email && <a href={`mailto:${settings.company_email}`} className="hover:text-[#F46A25]">{settings.company_email}</a>}
              </div>
              <div className="mt-1">
                {settings.website && <a href={settings.website.startsWith('http') ? settings.website : `https://${settings.website}`} className="hover:text-[#F46A25]">{settings.website}</a>} &nbsp;&middot;&nbsp; &copy; {new Date().getFullYear()} {brandName}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3" data-testid="footer-socials">
            {socials.length === 0 ? (
              <div className="text-[11px] text-neutral-500 italic">Add social links in Admin → Site settings</div>
            ) : socials.map(({ key, icon: Icon, label }) => (
              <a
                key={key}
                href={settings[key]}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                data-testid={`footer-${key}`}
                className="w-9 h-9 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#F46A25] transition-colors"
              >
                <Icon className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
